import { useMemo } from 'react';
import { SelectedDropdownFilters, SelectedFilter, TopLevelFilters } from '../constants/menu';
import { MenuCartItem } from '../models/menu.types';

interface Props {
  menuItems: MenuCartItem[];
  selectedFilters: SelectedFilter;
  selectedDropdownFilters: SelectedDropdownFilters;
  setFilteredResultCount: (count: number) => void;
}

export function useMenuFiltering({
  menuItems,
  selectedFilters,
  selectedDropdownFilters,
  setFilteredResultCount,
}: Props) {
  return useMemo(() => {
    // Hide/show meal type headers in menu page
    // TODO: add in real feature flag when available
    const isMealTypeHeaderVisible = false;

    // Filter by the top level filter (e.g. "Breakfast", "Lunch", "Dinner")
    const topLevelFilter: string = Object.keys(selectedFilters)[0];
    const secondLevelFilters: string[] = Object.values(selectedFilters)[0];
    // FILTER STEP 1: Start with menu items based on top level filter
    const topLevelFilteredMenuItems: MenuCartItem[] = menuItems.filter((menuItem) => {
      if (topLevelFilter === TopLevelFilters.QuickEats) {
        // Currently, setting TopLevelFilters.QuickEats = "Quick Eat" breaks the filtering
        // so for now, we'll manually check the two sub-filters that should roll up to "Quick Eats"
        return menuItem.filterTags.includes('Quick Eat') || menuItem.filterTags.includes('Side Dish');
      }
      return menuItem.filterTags.includes(topLevelFilter);
    });

    let secondLevelFilteredMenuItems: MenuCartItem[];

    // FILTER STEP 2: Factor in secondary pill filters
    // If View All/All is selected, we don't want to filter at all
    if ((topLevelFilter === TopLevelFilters.ViewAll)
      && (secondLevelFilters.includes('All'))) {
      secondLevelFilteredMenuItems = menuItems;
    } else if ((topLevelFilter !== TopLevelFilters.ViewAll) && (secondLevelFilters.includes('All'))) {
      // if top level is not ViewAll, but sub is "All"
      secondLevelFilteredMenuItems = topLevelFilteredMenuItems;
    } else if ((topLevelFilter === TopLevelFilters.ViewAll) && !(secondLevelFilters.includes('All'))) {
      // if we're viewing All but there are subfilter, we need to filter `menuItems`,
      // not `topLevelFiltered` - logical AND
      secondLevelFilteredMenuItems = menuItems.filter(
        (menuItem) => secondLevelFilters.every(
          (menuItemFilter) => menuItem.filterTags.map((filterTag) => filterTag.toLowerCase())
            .includes(menuItemFilter.toLowerCase()),
        ),
      );
    } else {
      // filter by the sub-filters when not Viewing All - logical AND
      secondLevelFilteredMenuItems = topLevelFilteredMenuItems.filter(
        (menuItem) => secondLevelFilters.every(
          (menuItemFilter) => menuItem.filterTags.map((filterTag) => filterTag.toLowerCase())
            .includes(menuItemFilter.toLowerCase()),
        ),
      );
    }

    // STEP 3: Dropdown filters
    const productTypeFilters: string[] = Object.values(selectedDropdownFilters.productType);
    const cookTimeFilters: string[] = Object.values(selectedDropdownFilters.cookTime);
    let fullyFilteredMenuItems: MenuCartItem[] = secondLevelFilteredMenuItems;

    if (productTypeFilters.length > 0) {
      fullyFilteredMenuItems = fullyFilteredMenuItems.filter((menuItem) => {
        if (productTypeFilters.includes('Grocery')) {
          return productTypeFilters.includes(menuItem.productType) || menuItem.productType === 'Bundle';
        }
        return productTypeFilters.includes(menuItem.productType);
      });
    }
    if (cookTimeFilters.length > 0) {
      fullyFilteredMenuItems = fullyFilteredMenuItems.filter((menuItem) => (
        cookTimeFilters.includes(menuItem.cookTime)
      ));
    }
    setFilteredResultCount(fullyFilteredMenuItems.length);

    // FINAL STEP: Divide items into sections if View All primary filter is selected
    // If top level filter is View All, partition items by precedence order and add divider on page
    let partitionedMenuItems: MenuCartItem[][] = [];
    let filterOrder = ['Dinner', 'Lunch', 'Breakfast', 'Quick Eats', 'Side Dish', 'Grocery'];
    if (topLevelFilter === TopLevelFilters.ViewAll) {
      partitionedMenuItems = filterOrder.map(() => []);

      // Finds earliest precedence category which item belongs to and adds to item
      // set partitionedMenuItems
      fullyFilteredMenuItems.forEach((item) => {
        let earliestIndex = filterOrder.length;
        item.filterTags.forEach((tag) => {
          const tagPrecedenceIndex = filterOrder.findIndex((category) => category === tag);
          if (tagPrecedenceIndex !== -1 && tagPrecedenceIndex < earliestIndex) {
            earliestIndex = tagPrecedenceIndex;
          }
        });
        partitionedMenuItems[
          earliestIndex === filterOrder.length
            ? filterOrder.length - 1
            : earliestIndex
        ].push(item);
      });

      // Merge filters for Quick Eats and Side Dish as Fall under 1 section
      const quickEatsIndex = filterOrder.findIndex((category) => category === 'Quick Eats');
      const sidesIndex = filterOrder.findIndex((category) => category === 'Side Dish');
      filterOrder = ['Dinner', 'Lunch', 'Breakfast', 'Quick Eats & Sides', 'Grocery'];

      partitionedMenuItems[quickEatsIndex] = [
        ...partitionedMenuItems[quickEatsIndex],
        ...partitionedMenuItems[sidesIndex]];

      partitionedMenuItems = partitionedMenuItems.filter((_elem, index) => index !== sidesIndex);
    }

    return {
      fullyFilteredMenuItems,
      partitionedMenuItems,
      filterOrder,
      isMealTypeHeaderVisible,
      topLevelFilter,
    };
  }, [
    menuItems,
    selectedFilters,
    selectedDropdownFilters,
    setFilteredResultCount,
  ]);
}
